// SF1 — Order Status Overview. An operations board, not a report.
//
// No date filter anywhere, deliberately. Everything else in this section
// reports a rate over a window; this reports state now. An order placed five
// days ago and still unshipped is the most urgent thing here, and a filter set
// to today would remove it.
//
// The board can also be UNCONFIRMED. Amazon answers a throttled read with an
// empty payload that looks exactly like having nothing outstanding, so an empty
// board is checked against the last synced report before it is allowed to read
// as an all-clear. Telling someone their queue is clear when the read failed is
// the worst thing this screen could do.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  FiAlertTriangle, FiCheckCircle, FiChevronDown, FiChevronRight, FiInfo, FiRefreshCw,
} from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderFulfillment.module.css';
import { getOrderBoard } from '../services/orderAnalyticsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const count = (value) =>
  value === null || value === undefined ? '—' : inr.format(value);

const money = (value) =>
  value === null || value === undefined ? '—' : `₹${inr.format(Math.round(value))}`;

const PIPELINE_LABELS = {
  unshipped_merchant: 'Unshipped, yours to send',
  unshipped_amazon: 'Unshipped, Amazon sending',
  partially_shipped: 'Partially shipped',
  invoice_unconfirmed: 'Awaiting invoice confirmation',
  pending_authorisation: 'Pending payment authorisation',
  unfulfillable: 'Amazon cannot fulfil',
};

const formatWhen = (iso) => {
  if (!iso) return 'never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

// A figure from today beside the baseline that makes it readable. A bare number
// says nothing about whether the day is going well.
const TodayMetric = ({ label, metric, backfilling }) => {
  const baseline = metric?.baseline;
  const value = metric?.value;
  const comparable = baseline?.value !== null && baseline?.value !== undefined
    && value !== null && value !== undefined;
  const up = comparable && value > baseline.value;

  return (
    <div className={local.todayTile}>
      <div className={local.todayLabel}>{label}</div>
      <div className={local.todayValue}>
        {value === null || value === undefined ? '—' : count(value)}
      </div>
      <div className={local.todayFoot}>
        {metric?.reason ? metric.reason
          : backfilling ? 'no baseline yet — still syncing your history'
          : comparable ? (
            <>
              <span className={up ? local.up : local.down}>
                {up ? 'above' : 'below'}
              </span>
              {' '}the usual {count(baseline.value)} · {baseline.basis}
            </>
          ) : (baseline?.basis || 'no baseline yet')}
      </div>
    </div>
  );
};

const OrderBoard = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async (refresh = false) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getOrderBoard(token, { refresh }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.needs_action || [];
  const pipeline = data?.pipeline?.buckets || {};
  const today = data?.today;

  return (
    <>
      <section className={local.card}>
        <div className={local.cardHead}>
          <h2 className={local.cardTitle}>Needs action now</h2>
          <div className={local.headAside}>
            <span className={local.muted}>
              Read {formatWhen(data?.as_of)}
            </span>
            <button type="button" className={local.ghost}
              onClick={() => load(true)} disabled={loading}>
              <FiRefreshCw /> {loading ? 'Reading…' : 'Refresh'}
            </button>
          </div>
        </div>
        <p className={local.muted}>
          Everything still open, whatever its age. There is no date filter here
          on purpose — the oldest unshipped order is the most urgent one.
        </p>

        {error && (
          <div className={`${local.banner} ${local.bannerError}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>
              <strong>
                {error.notConnected ? 'No Amazon account connected' : 'Could not load the board'}
              </strong>
              <div>{error.message}</div>
            </div>
          </div>
        )}

        {/* The load-bearing state. An unconfirmed board must never be mistaken
            for an empty queue. */}
        {data && data.confirmed === false && (
          <div className={`${local.banner} ${local.bannerWarn}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>
              <strong>This board is not confirmed</strong>
              <div>
                Amazon did not answer the live read, so what you see below is not
                your current position. Refresh in a moment rather than treating
                it as nothing to do.
              </div>
            </div>
          </div>
        )}

        {!error && data?.all_clear && (
          <div className={local.allClear}>
            <FiCheckCircle />
            Nothing needs you right now. No orders are overdue, blocked or
            waiting on an invoice, and none have come in today.
          </div>
        )}

        {!error && data?.dispatch_rows_apply === false && (
          <div className={local.allClear}>
            <FiCheckCircle />
            {data.amazon_only_reason}
          </div>
        )}

        {!error && data && (
          <div className={local.actionRows}>
            {rows.map((row) => (
              <div key={row.key} className={row.count ? local.actionRow : local.actionRowEmpty}>
                <button type="button" className={local.actionMain}
                  onClick={() => setExpanded(expanded === row.key ? null : row.key)}
                  disabled={!row.count}>
                  <span className={local.actionChevron}>
                    {row.count ? (expanded === row.key ? <FiChevronDown /> : <FiChevronRight />) : null}
                  </span>
                  <span className={local.actionLabel}>{row.label}</span>
                  <span className={local.actionCount}>{count(row.count)}</span>
                  <span className={local.actionValue}>
                    {money(row.value)}
                    {!row.value_complete && row.count > 0 && (
                      <span className={local.partial} title="Amazon did not price every order in this row">
                        part-priced
                      </span>
                    )}
                  </span>
                  <span className={local.actionAge}>
                    {row.count ? `oldest ${row.oldest_days}d` : ''}
                  </span>
                </button>
                {expanded === row.key && row.orders?.length > 0 && (
                  <div className={local.drill}>
                    {row.orders.map((order) => (
                      <div key={order.order_id} className={local.drillRow}>
                        <span className={local.drillId}>{order.order_id}</span>
                        <span>{order.channel}</span>
                        <span>{order.age_days}d old</span>
                        <span>{order.ship_by ? `due ${formatWhen(order.ship_by)}` : 'no deadline'}</span>
                        <span className={local.numeric}>{money(order.value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {today && (
        <section className={local.card}>
          <div className={local.cardHead}>
            <h2 className={local.cardTitle}>Today so far</h2>
            <span className={local.istTag}>from IST midnight</span>
          </div>
          <div className={local.todayRow}>
            <TodayMetric label="Orders received" metric={today.orders_received}
              backfilling={today.backfilling} />
            <TodayMetric label="Cancelled" metric={today.cancelled}
              backfilling={today.backfilling} />
            <TodayMetric label="Returns raised" metric={today.returns_raised}
              backfilling={today.backfilling} />
          </div>
          <p className={local.note}>{today.note}</p>
        </section>
      )}

      {data?.pipeline && (
        <section className={local.card}>
          <div className={local.cardHead}>
            <h2 className={local.cardTitle}>Open pipeline</h2>
            <span className={local.muted}>{count(data.pipeline.total)} orders open</span>
          </div>
          <p className={local.muted}>
            Everything not yet closed, with no time bound. These add up to the
            total above.
          </p>
          <div className={local.pipelineRow}>
            {Object.entries(PIPELINE_LABELS).map(([key, label]) => (
              <div key={key} className={local.pipelineTile}>
                <div className={local.todayLabel}>{label}</div>
                <div className={local.todayValue}>{count(pipeline[key] || 0)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </>
  );
};

export default OrderBoard;
